import { useEffect, useId, useMemo } from 'react';

import { create } from 'zustand';

import {
  SITE_CONTROL_SIDEBAR_DATA_ATTR,
  SITE_CONTROL_SIDEBAR_TOGGLE_ATTR,
} from './useSidebarNavTabIndexWhenHidden.ts';

type OverlaySideBarStore = {
  keepOpen: boolean;
  keepOpenLocks: ReadonlySet<string>;
  acquireKeepOpen: (id: string) => void;
  releaseKeepOpen: (id: string) => void;
};

export const useOverlaySideBarStore = create<OverlaySideBarStore>(set => ({
  keepOpen: false,
  keepOpenLocks: new Set(),
  acquireKeepOpen: id =>
    set(state => {
      if (state.keepOpenLocks.has(id)) return state;
      const keepOpenLocks = new Set(state.keepOpenLocks);
      keepOpenLocks.add(id);
      return { keepOpenLocks, keepOpen: true };
    }),
  releaseKeepOpen: id =>
    set(state => {
      if (!state.keepOpenLocks.has(id)) return state;
      const keepOpenLocks = new Set(state.keepOpenLocks);
      keepOpenLocks.delete(id);
      return { keepOpenLocks, keepOpen: keepOpenLocks.size > 0 };
    }),
}));

/** One keep-open lock for this component instance; released automatically on unmount. */
export const useOverlaySideBarKeepOpenLock = () => {
  const lockId = useId();
  const acquireKeepOpen = useOverlaySideBarStore(s => s.acquireKeepOpen);
  const releaseKeepOpen = useOverlaySideBarStore(s => s.releaseKeepOpen);

  useEffect(() => () => releaseKeepOpen(lockId), [lockId, releaseKeepOpen]);

  return useMemo(
    () => ({
      acquire: () => acquireKeepOpen(lockId),
      release: () => releaseKeepOpen(lockId),
    }),
    [acquireKeepOpen, lockId, releaseKeepOpen]
  );
};

/** Docks the overlay sidebar and holds a keep-open lock until this view unmounts. */
export const useForceOverlaySideBarOpen = () => {
  const { acquire } = useOverlaySideBarKeepOpenLock();

  useEffect(() => {
    dockOverlaySideBar();
    acquire();
  }, [acquire]);
};

/** Clicks Scale UI's sidebar toggle only when the overlay is not already docked. */
export const dockOverlaySideBar = () => {
  const aside = document.querySelector<HTMLElement>(`aside[${SITE_CONTROL_SIDEBAR_DATA_ATTR}]`);
  if (aside && aside.getAttribute('aria-hidden') !== 'true') return;

  document.querySelector<HTMLButtonElement>(`button[${SITE_CONTROL_SIDEBAR_TOGGLE_ATTR}]`)?.click();
};
